import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Check, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BUSINESS_FEATURES = [
  "Réunions illimitées",
  "Transcription prioritaire",
  "Export PDF des comptes-rendus",
  "Actions & décisions automatiques",
  "Assistant IA illimité",
  "Support prioritaire",
];

export default function BillingPage() {
  const [info, setInfo] = useState(null);
  const [paying, setPaying] = useState(false);
  const [providerOpen, setProviderOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/info").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);

  const startCheckout = async (provider) => {
    setPaying(true);
    setProviderOpen(false);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: "business",
        origin_url: window.location.origin,
        provider,
      });
      window.location.href = data.url;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de lancer le paiement");
      setPaying(false);
    }
  };

  const onUpgrade = () => setProviderOpen(true);

  if (!info) return <div className="p-10 text-af-muted">Chargement…</div>;

  const isBusiness = info.plan === "business";
  const used = info.meetings_used ?? 0;
  const limit = info.meetings_limit;

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Abonnement</div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">Votre formule</h1>
        <p className="text-af-secondary mt-2">Gérez votre abonnement et votre consommation.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Current / Free plan */}
        <Card className="p-8 bg-af-card border-af rounded-md" data-testid="billing-current-plan">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest text-af-muted font-semibold">Starter</div>
            {!isBusiness && (
              <Badge className="bg-[#F5EFD8] text-[#3A2E1F] border border-[#E5D9B8] rounded-sm">Formule actuelle</Badge>
            )}
          </div>
          <div className="font-display font-extrabold text-3xl mb-1">Essai gratuit</div>
          <div className="text-sm text-af-muted mb-6">2 réunions incluses pour découvrir la plateforme</div>
          {!isBusiness && (
            <div className="mb-6" data-testid="billing-usage">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-af-secondary">Réunions utilisées</span>
                <span className="font-semibold">{Math.min(used, limit)} / {limit}</span>
              </div>
              <Progress value={Math.min((used / limit) * 100, 100)} className="h-2" />
              {used >= limit && (
                <p className="text-xs text-[#9F1239] mt-2 font-medium" data-testid="billing-quota-reached">
                  Vous avez atteint la limite de votre essai gratuit.
                </p>
              )}
            </div>
          )}
          <ul className="space-y-2.5 text-sm text-af-secondary">
            {["2 réunions au total", "Transcription automatique", "Résumé intelligent IA"].map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <Check className="h-4 w-4 text-terracotta shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
        </Card>

        {/* Business plan */}
        <Card className="p-8 bg-[#2B1F12] text-white border-[#2B1F12] rounded-md relative overflow-hidden" data-testid="billing-business-plan">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest text-white/60 font-semibold">Business</div>
            {isBusiness ? (
              <Badge className="bg-terracotta text-white border-0 rounded-sm" data-testid="billing-business-active">
                <Crown className="h-3 w-3 mr-1" /> Formule active
              </Badge>
            ) : (
              <Badge className="bg-terracotta/20 text-[#D4A04E] border border-terracotta/40 rounded-sm">Recommandé</Badge>
            )}
          </div>
          <div className="font-display font-extrabold text-3xl mb-1">
            35 000 <span className="text-lg text-white/60">FCFA</span>
            <span className="text-sm font-normal text-white/60"> / mois</span>
          </div>
          <div className="text-sm text-white/60 mb-6">Pour les équipes professionnelles</div>
          <ul className="space-y-2.5 text-sm text-white/85 mb-8">
            {BUSINESS_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <Check className="h-4 w-4 text-[#D4A04E] shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
          {isBusiness ? (
            <div className="space-y-4">
              <div className="text-sm text-white/75 flex items-start gap-2" data-testid="billing-expiry-info">
                <Sparkles className="h-4 w-4 text-[#D4A04E] shrink-0 mt-0.5" />
                <span>
                  {info.plan_expires_at
                    ? <>Abonnement actif jusqu'au{" "}
                        <strong>{new Date(info.plan_expires_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</strong>
                        {" "}({info.days_remaining} jour{info.days_remaining > 1 ? "s" : ""} restant{info.days_remaining > 1 ? "s" : ""})</>
                    : "Réunions illimitées actives."}
                </span>
              </div>
              <Button
                onClick={onUpgrade}
                disabled={paying}
                data-testid="billing-renew-btn"
                className="w-full bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11 font-semibold"
              >
                {paying ? "Redirection vers le paiement…" : "Renouveler 30 jours — 35 000 FCFA"}
              </Button>
              <p className="text-xs text-white/50 text-center">
                Le renouvellement ajoute 30 jours à votre abonnement actuel. Sans renouvellement, vous repassez au plan gratuit à l'expiration.
              </p>
            </div>
          ) : (
            <Button
              onClick={onUpgrade}
              disabled={paying}
              data-testid="billing-upgrade-btn"
              className="w-full bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11 font-semibold"
            >
              {paying ? "Redirection vers le paiement…" : "Passer au plan Business"}
            </Button>
          )}
          {!isBusiness && (
            <p className="text-xs text-white/50 text-center mt-3">Paiement sécurisé par carte bancaire (Stripe)</p>
          )}
        </Card>
      </div>

      {/* Provider picker dialog */}
      {providerOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="provider-dialog">
          <Card className="bg-af-card border-af rounded-md p-7 max-w-md w-full">
            <h3 className="font-display font-bold text-lg mb-1">Choisissez votre moyen de paiement</h3>
            <p className="text-sm text-af-secondary mb-6">35 000 FCFA pour 30 jours d'accès Business.</p>
            <button
              onClick={() => startCheckout("stripe")}
              data-testid="pay-stripe-btn"
              className="w-full text-left border border-af rounded-md p-4 mb-3 hover:border-terracotta hover:bg-[#F5EFD8] transition-colors flex items-center gap-3"
            >
              <div className="h-10 w-10 rounded-md bg-[#635BFF] flex items-center justify-center text-white font-bold text-sm shrink-0">
                S
              </div>
              <div className="flex-1">
                <div className="font-semibold text-af">Carte bancaire</div>
                <div className="text-xs text-af-muted mt-1">Visa / Mastercard / Amex — via Stripe</div>
              </div>
            </button>
            <button
              onClick={() => toast.info("Wave — Disponible bientôt", { description: "Le paiement par Wave Mobile Money arrive prochainement." })}
              data-testid="pay-wave-btn"
              className="w-full text-left border border-af border-dashed rounded-md p-4 hover:border-terracotta transition-colors flex items-center gap-3 relative opacity-90"
            >
              <div className="h-10 w-10 rounded-md overflow-hidden shrink-0 bg-[#1DC8F2]">
                <img
                  src="/wave-logo.png"
                  alt="Wave"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-af flex items-center gap-2">
                  Wave Mobile Money
                  <span className="text-[10px] uppercase tracking-wider bg-[#F5EFD8] text-[#3A2E1F] border border-[#E5D9B8] px-2 py-0.5 rounded-sm font-semibold">Bientôt</span>
                </div>
                <div className="text-xs text-af-muted mt-1">Paiement instantané via Wave — Disponible bientôt</div>
              </div>
            </button>
            <div className="flex justify-end mt-5">
              <Button variant="ghost" onClick={() => setProviderOpen(false)}>Annuler</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
